import React, { useEffect, useState } from "react";
import PopUpModal from "../SharedComponents/PopUpModal";
import CreateClassRequest from "./CreateClassRequest";
import ConfirmClassRequest from "./ConfirmClassRequest";
import SuccessMessage from "./SuccessMessage";
import { API } from "../../Services/api-services";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { Spinner } from "react-bootstrap";
import { useStyles } from "./style";
import { fireEpicenterGtm } from "../../datalayers";

const ClassRequestMentor = ({ showModal, setShowCreateModal, initialStudentId, initialCourseId }) => {
  const [classReqdata, setClassReqdata] = useState({
    student: { key: "", text: "", value: "" },
    courses: { key: "", text: "", value: "" },
    mentorTime: null,
    studentTime: null,
  });

  const [courseList, setCourseList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [warningMsg, setWarningMsg] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setClassReqdata({
      student: { key: "", text: "", value: "" },
      courses: { key: "", text: "", value: "" },
      mentorTime: null,
      studentTime: null,
    });
    setCourseList([]);
    setWarningMsg("");
  };
  const [loader, setLoader] = useState(false);
  const classes = useStyles();

  const convertDateToISO = (date, timezoneTo) => {
    const parsedDate = moment.tz(date, "YYYY-MM-DD hh:mm A", timezoneTo);
    return toIsoWithOffset(parsedDate.toISOString(), timezoneTo);
  };
  function toIsoWithOffset(time, timezone) {
    return moment.tz(time, timezone).format("YYYY-MM-DDTHH:mm:ssZ");
  }
  const handleProceedCreateModal = () => {
    if (
      classReqdata.student.text.length === 0 ||
      classReqdata.courses.text.length === 0 ||
      !classReqdata.mentorTime ||
      !classReqdata.studentTime
    ) {
      setWarningMsg("Please fill in all the fields to proceed");
    } else {
      setWarningMsg("");
      setShowCreateModal(false);
      setShowConfirmModal(true);
    }
  };
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };
  const handleCancelConfirmModal = () => {
    setShowConfirmModal(false);
    setShowCreateModal(true);
    setWarningMsg("");
  };

  const handleSendRequest = async () => {
    const batchIdListInverted = Object.fromEntries(
      Object.entries(classReqdata?.student?.batchIdList).map(([key, value]) => [
        value,
        key,
      ])
    );
    const formData = {
      active: 1,
      batchId: batchIdListInverted[classReqdata?.courses?.courseId],
      timingIst: toIsoWithOffset(classReqdata?.mentorTime, "Asia/Kolkata"),
      timingStudentTz: convertDateToISO(
        classReqdata?.studentTime,
        classReqdata?.student?.timezone
      ),
      courseId: classReqdata?.courses?.courseId,
      orderId: classReqdata?.courses?.orderId,
      studentId: classReqdata?.student?.studentId,
      studentName: classReqdata?.student?.studentName,
      studentEmail: classReqdata?.student?.studentEmail,
      requestType: 3,
      viewWarning: warningMsg.length > 0,
    };

    try {
      setLoader(true);
      await API.post("/loop/mentor/request", { ...formData });
      // Shared with the Loop page; initialStudentId is only set when the flow was
      // opened from a My students card, which is the surface the sheet tracks.
      if (initialStudentId) {
        fireEpicenterGtm("student_class_request_submitted", [
          "",
          "my_students",
          classReqdata?.courses?.text || "",
        ]);
      }
      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (err) {
      if (err?.response?.data?.mentorLeaveDates) {
        setWarningMsg(err?.response?.data?.msg);
      } else {
        toast.error(err?.response?.data?.msg, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setShowConfirmModal(false);
      }
    } finally {
      setLoader(false);
    }
  };
  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    window.location.reload();
  };

  const fetchFormData = async () => {
    try {
      const res = await API.get("/loop/mentor/formData");
      if (res?.data?.data && res?.data?.data?.length > 0) {
        const studentDataReq = res.data.data.map((item) => {
          return {
            key: item.studentId,
            value: item.studentId,
            text: item.studentName,
            ...item,
          };
        });
        setStudentList(studentDataReq);
      }
    } catch (e) {
      console.error("Error while fetching formData", e);
    }
  };

  useEffect(() => {
    fetchFormData();
  }, []);

  // Auto-select student and course when opened from a student card
  useEffect(() => {
    if (!initialStudentId || studentList.length === 0) return;

    const selectedStudent = studentList.find(
      (item) => item.studentId === initialStudentId
    );
    if (!selectedStudent) return;

    const courses = (selectedStudent.courses || []).map((course) => ({
      key: course.courseId,
      value: course.courseId,
      text: course.courseName,
      ...course,
    }));
    setCourseList(courses);
    setClassReqdata((prev) => ({ ...prev, student: selectedStudent }));

    if (initialCourseId && courses.length > 0) {
      const selectedCourse = courses.find(
        (c) => c.courseId === initialCourseId
      );
      if (selectedCourse) {
        setClassReqdata((prev) => ({ ...prev, student: selectedStudent, courses: selectedCourse }));
      }
    }
  }, [initialStudentId, initialCourseId, studentList]);

  return (
    <div>
      <PopUpModal
        open={showModal}
        title={"Create class request"}
        okBtnText={"Proceed"}
        showCancelBtn={false}
        handleOkBtn={handleProceedCreateModal}
        handleClose={handleCloseCreateModal}
      >
        <CreateClassRequest
          classReqdata={classReqdata}
          setClassReqdata={setClassReqdata}
          warningMsg={warningMsg}
          courseList={courseList}
          setCourseList={setCourseList}
          studentList={studentList}
          setWarningMsg={setWarningMsg}
        />
      </PopUpModal>
      <PopUpModal
        open={showConfirmModal}
        title={"Confirm creating class request"}
        cancelBtnText={"Go back"}
        okBtnText={warningMsg.length ? "Send request anyway" : "Send request"}
        handleClose={handleCloseConfirmModal}
        handleCancelBtn={handleCancelConfirmModal}
        handleOkBtn={handleSendRequest}
      >
        {loader ? (
          <div className={classes.spinnerContainer}>
            <Spinner animation="border" variant="secondary" className="my-3" />
          </div>
        ) : (
          <ConfirmClassRequest
            classReqdata={classReqdata}
            warningMsg={warningMsg}
            message={
              "Please review the class details before sending the request"
            }
          />
        )}
      </PopUpModal>
      <PopUpModal
        open={showSuccessModal}
        showCloseIcon={false}
        showCancelBtn={false}
        okBtnText={"Close"}
        handleOkBtn={closeSuccessModal}
      >
        <SuccessMessage
          message={
            "Class request sent! You’ll be notified once it’s confirmed."
          }
          showMessage={true}
          title={"Success!"}
        />
      </PopUpModal>
    </div>
  );
};

export default ClassRequestMentor;
